/**
 * Create or edit a data model of your own (a "virtual model") in a window: a name and fields; each field has a label and the
 * source fields (class · path) of the systems that mean the same thing. Sources are picked from what the current environment
 * has actually received. The window is a step in history (the caller keeps `?edit=` in the URL); what is typed is kept
 * in sessionStorage per model until it is saved or cancelled. The rules are in lib/modeledit.ts.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Api, ApiError, type VirtualModel } from '../lib/api.ts';
import { useAsync } from '../lib/hooks.ts';
import { useEnv } from '../lib/envs.tsx';
import { useFormDraft } from '../lib/formstate.ts';
import { count } from '../lib/format.ts';
import { noun } from '../lib/nouns.ts';
import {
  LIMITS, addSource, buildPayload, draftKey, emptyField, emptyModel, filterFields, hasSource, move, problemCount, removeField, removeSource, setLabel, sourceKey, systemOfClass, toEdit, validate,
  type EditDraft, type EditField, type EditModel, type Source,
} from '../lib/modeledit.ts';
import { IconButton, SystemBadge, cx } from '../design/primitives.tsx';
import { ChevronIcon, CloseIcon, PlusIcon, SearchIcon, TrashIcon } from './icons.tsx';
import { Button, Field, Spinner, inputClass, useToast } from './ui.tsx';

/** One field of the current environment's discovered model, for the picker. */
interface Known { cls: string; path: string; count: number; messages: number }

const PICKER_ROWS = 200;
const live = /^(At most|Another)/;

// ---------------------------------------------------------------- the source picker

/** A searchable list of every field of the environment's data models; Enter or a click adds, the list stays open for the next one. */
function SourcePicker({ known, loading, error, field, onAdd, onClose, envName }: {
  known: Known[]; loading: boolean; error: string | undefined; field: EditField; onAdd: (s: Source) => void; onClose: () => void; envName: string;
}) {
  const id = useId();
  const [query, setQuery] = useState('');
  const [cls, setCls] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const classes = useMemo(() => [...new Map(known.map((k) => [k.cls, k.messages])).entries()].sort((a, b) => a[0].localeCompare(b[0])), [known]);
  const { shown, total } = useMemo(() => filterFields(known, query, cls || undefined, PICKER_ROWS), [known, query, cls]);
  const at = Math.min(active, Math.max(0, shown.length - 1));
  useEffect(() => { listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' }); }, [at, shown]);
  const optionId = (i: number) => `${id}-o${i}`;
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(Math.min(shown.length - 1, at + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(Math.max(0, at - 1)); }
    else if (e.key === 'PageDown') { e.preventDefault(); setActive(Math.min(shown.length - 1, at + 8)); }
    else if (e.key === 'PageUp') { e.preventDefault(); setActive(Math.max(0, at - 8)); }
    else if (e.key === 'Enter') { e.preventDefault(); const k = shown[at]; if (k && !hasSource(field, k)) onAdd(k); }
  };
  // Rows grouped by class, in the order of the list
  const groups: { cls: string; rows: { k: Known; i: number }[] }[] = [];
  shown.forEach((k, i) => {
    const g = groups[groups.length - 1];
    if (g && g.cls === k.cls) g.rows.push({ k, i }); else groups.push({ cls: k.cls, rows: [{ k, i }] });
  });
  return (
    <div className="rounded-chip border border-n4 bg-n2 flex flex-col mv-rise" data-testid="source-picker">
      <div className="flex items-center gap-2 p-2 flex-wrap">
        <label className="flex-1 min-w-[220px] flex items-center gap-2 h-9 px-3 rounded-chip bg-n3 text-n8 focus-within:ring-[1.5px] focus-within:ring-accent">
          <SearchIcon size={14} /><span className="sr-only">Find a source field</span>
          <input autoFocus role="combobox" aria-expanded="true" aria-controls={`${id}-list`} aria-autocomplete="list" aria-activedescendant={shown.length ? optionId(at) : undefined}
            className="flex-1 min-w-0 bg-transparent border-0 outline-none text-small text-n10 placeholder:text-n7" placeholder="Type to find: class or field name"
            value={query} onChange={(e) => { setQuery(e.target.value); setActive(0); }} onKeyDown={onKey} />
        </label>
        <label className="flex items-center gap-2 text-small text-n8">
          <span className="sr-only sm:not-sr-only">Class</span>
          <select className={cx(inputClass, 'w-52')} value={cls} onChange={(e) => { setCls(e.target.value); setActive(0); }} aria-label="Limit to a class">
            <option value="">All classes ({classes.length})</option>
            {classes.map(([c]) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <Button variant="ghost" onClick={onClose} aria-label="Close the picker">Done</Button>
      </div>
      {loading && <div className="py-4 grid place-items-center"><Spinner /></div>}
      {error && <p className="m-0 px-3 pb-3 text-small text-fail">{error}</p>}
      {!loading && !error && known.length === 0 && <p className="m-0 px-3 pb-3 text-small text-n8">{envName} has not received any documents with fields yet, so there is nothing to pick from.</p>}
      {!loading && known.length > 0 && shown.length === 0 && <p className="m-0 px-3 pb-3 text-small text-n8">No field matches “{query}”.</p>}
      {shown.length > 0 && (
        <ul ref={listRef} id={`${id}-list`} role="listbox" aria-label="Fields of the environment" className="m-0 p-0 pb-1 list-none max-h-64 overflow-y-auto border-t border-n4">
          {groups.map((g) => (
            <li key={g.cls} role="presentation">
              <div className="sticky top-0 z-10 bg-n2 px-3 py-1.5 flex items-center gap-2 text-micro text-n7 border-b border-n4/60">
                <SystemBadge code={systemOfClass(g.cls)} size={20} />
                <span className="font-mono text-n9">{g.cls}</span><span>· {noun(g.cls, 2)}</span>
              </div>
              <ul role="group" aria-label={g.cls} className="m-0 p-0 list-none">
                {g.rows.map(({ k, i }) => {
                  const added = hasSource(field, k);
                  return (
                    <li key={k.path} id={optionId(i)} role="option" aria-selected={i === at} aria-disabled={added}
                      onMouseMove={() => i !== at && setActive(i)} onClick={() => !added && onAdd(k)}
                      className={cx('flex items-center gap-3 px-3 min-h-9 cursor-pointer', i === at && 'bg-accent-soft', added && 'cursor-default opacity-60')}>
                      <span className="flex-1 min-w-0 font-mono text-small text-n10 [overflow-wrap:anywhere]">{k.path}</span>
                      <span className="text-micro text-n7 whitespace-nowrap tabular-nums">{count(k.count)} documents</span>
                      <span className="w-14 text-right text-micro text-n8">{added ? 'Added' : i === at ? 'Enter' : ''}</span>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
      {total > shown.length && <p className="m-0 px-3 py-2 text-micro text-n7 border-t border-n4">Showing {shown.length} of {count(total)} fields. Type to narrow the list.</p>}
    </div>
  );
}

// ---------------------------------------------------------------- one field

export function SourceChip({ s, unseen, onRemove }: { s: Source; unseen?: boolean; onRemove?: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 h-7 pl-1 pr-1 rounded-full bg-n3 text-n9 font-mono text-micro max-w-full" title={`${noun(s.cls)} · ${s.cls}${unseen ? ' (not seen in this environment)' : ''}`}>
      <SystemBadge code={s.system ?? systemOfClass(s.cls)} size={20} />
      <span className="truncate">{s.cls}<span className="text-n6">·</span>{s.path}</span>
      {unseen && <span className="size-1.5 rounded-full bg-warn shrink-0" role="img" aria-label="Not seen in this environment" />}
      {onRemove && (
        <button type="button" onClick={onRemove} aria-label={`Remove ${s.cls} ${s.path}`}
          className="size-5 rounded-full grid place-items-center border-0 bg-transparent text-n7 cursor-pointer hover:bg-n4 hover:text-n10 focus-visible:ring-2 focus-visible:ring-accent"><CloseIcon size={12} /></button>
      )}
    </span>
  );
}

// ---------------------------------------------------------------- the window

export function ModelEditor({ tid, id, model, start, draftId, fromSuggestion, onClose, onSaved }: {
  tid: string;
  /** 'new' or the id of the model whose draft is edited. */
  id: string;
  model?: VirtualModel;
  /** A new model's starting point (a suggestion) instead of an empty one. */
  start?: EditModel;
  /** The sessionStorage draft's id when it is not `id` (one per suggestion). */
  draftId?: string;
  /** The suggestion the new model is made from: sent with the save, so the acceptance is audited. */
  fromSuggestion?: string;
  /** The window was closed without saving (X, Esc, Cancel): the caller steps back. */
  onClose: () => void;
  onSaved: (saved: VirtualModel) => void;
}) {
  const toast = useToast();
  const { current } = useEnv();
  const key = draftKey(tid, draftId ?? id);
  const initial = useMemo<EditDraft>(() => (model ? toEdit(model) : start ? toEdit(start) : emptyModel()), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [draft, setDraft, clearDraft] = useFormDraft<EditDraft>(key, initial);
  const [picker, setPicker] = useState<number | null>(null);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const nameRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const focusAfter = useRef<string | undefined>(undefined);
  const discovered = useAsync(() => Api.model(tid), [tid, current?.id]);
  const known = useMemo<Known[]>(() => (discovered.data?.classes ?? []).flatMap((c) => [...c.fields].sort((a, b) => b.count - a.count).map((f) => ({ cls: c.name, path: f.path, count: f.count, messages: c.messages }))), [discovered.data]);
  const knownKeys = useMemo(() => new Set(known.map(sourceKey)), [known]);
  const problems = validate(draft);
  const shown = {
    name: problems.name && (tried || live.test(problems.name)) ? problems.name : undefined,
    fields: problems.fields && tried ? problems.fields : undefined,
    label: (i: number) => { const m = problems.perField[i]?.label; return m && (tried || live.test(m)) ? m : undefined; },
    sources: (i: number) => { const m = problems.perField[i]?.sources; return m && (tried || live.test(m)) ? m : undefined; },
  };
  const fields = draft.fields;
  const setFields = (f: EditField[]) => setDraft({ fields: f });

  useEffect(() => {
    const target = focusAfter.current;
    focusAfter.current = undefined;
    if (target) bodyRef.current?.querySelector<HTMLElement>(`[data-focus="${target}"]`)?.focus();
  });

  const cancel = () => { clearDraft(); onClose(); };
  const submit = async () => {
    setTried(true);
    if (!problems.ok) {
      focusAfter.current = problems.name ? 'name' : `label-${Math.max(0, problems.perField.findIndex((p) => p.label || p.sources))}`;
      setDraft({}); // re-render so the focus effect runs
      return;
    }
    setBusy(true);
    setFailure(undefined);
    try {
      const saved = await Api.saveVirtualModel(tid, { ...buildPayload(id === 'new' ? undefined : id, draft), ...(fromSuggestion ? { fromSuggestion } : {}) });
      toast('Saved as a draft. Publish it to version it');
      clearDraft();
      onSaved(saved);
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  };
  const total = problemCount(problems);
  const title = fromSuggestion ? 'New data model from a suggestion' : id === 'new' ? 'New data model' : `Edit ${model?.name ?? 'data model'}`;
  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) cancel(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]" />
        <Dialog.Content aria-describedby="model-editor-help"
          onOpenAutoFocus={(e) => { e.preventDefault(); nameRef.current?.focus(); }}
          onEscapeKeyDown={(e) => { if (picker !== null) { e.preventDefault(); setPicker(null); } }}
          className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(860px,94vw)] max-h-[90vh] flex flex-col bg-n2 text-n10 rounded-card shadow-float outline-none mv-pop">
          <form className="flex flex-col min-h-0" onSubmit={(e) => { e.preventDefault(); void submit(); }} noValidate>
            <div className="flex items-start gap-3 p-5 pb-3">
              <div className="flex-1 min-w-0 flex flex-col gap-1">
                <Dialog.Title className="m-0 text-body font-semibold">{title}</Dialog.Title>
                <p id="model-editor-help" className="m-0 text-small text-n8 max-w-2xl">
                  {fromSuggestion
                    ? 'Pre-filled from what your data has in common. Check the fields and their sources, remove what does not belong and rename what reads oddly; you save a draft, publishing makes it a version.'
                    : 'Each field is one thing (a customer name) and lists where it lives in each system. A search through the model finds records by any of them. You save a draft; publishing makes it a version.'}
                </p>
              </div>
              <Dialog.Close asChild><IconButton label="Close the editor"><CloseIcon size={16} /></IconButton></Dialog.Close>
            </div>

            <div ref={bodyRef} className="px-5 pb-3 flex flex-col gap-4 overflow-y-auto min-h-0">
              <Field label="Name" error={shown.name} hint={`${draft.name.trim().length}/${LIMITS.name}`} className="max-w-md">
                <input ref={nameRef} data-focus="name" className={cx(inputClass, shown.name && 'ring-[1.5px] ring-fail')} value={draft.name} aria-invalid={!!shown.name} placeholder="Customer"
                  onChange={(e) => setDraft({ name: e.target.value })} />
              </Field>

              <div className="flex flex-col gap-2.5">
                <div className="flex items-baseline gap-2">
                  <h3 className="m-0 text-small font-semibold">Fields</h3>
                  <span className="text-micro text-n7">{fields.length} of {LIMITS.fields}</span>
                </div>
                {shown.fields && <p className="m-0 text-small text-fail" role="alert">{shown.fields}</p>}
                <ol className="m-0 p-0 list-none flex flex-col gap-3" aria-label="Fields of the data model">
                  {fields.map((f, i) => {
                    const labelError = shown.label(i), sourceError = shown.sources(i);
                    return (
                      <li key={i} className="rounded-chip bg-n1/60 border border-n4 p-3 flex flex-col gap-2.5" aria-label={f.label.trim() || `Field ${i + 1}`}>
                        <div className="flex items-start gap-2">
                          <span className="h-9 w-5 grid place-items-center text-micro text-n7 tabular-nums shrink-0" aria-hidden="true">{i + 1}</span>
                          <Field label="Label" error={labelError} className="flex-1 min-w-0 [&>span:first-child]:sr-only">
                            <input data-focus={`label-${i}`} className={cx(inputClass, labelError && 'ring-[1.5px] ring-fail')} value={f.label} aria-invalid={!!labelError} placeholder="Label, e.g. Customer name"
                              onChange={(e) => setFields(setLabel(fields, i, e.target.value))} />
                          </Field>
                          <div className="flex items-center gap-0.5 shrink-0 pt-0.5" role="group" aria-label={`Field ${i + 1} actions`}>
                            <IconButton label={`Move ${f.label.trim() || `field ${i + 1}`} up`} data-focus={`up-${i}`} disabled={i === 0} className="disabled:opacity-30 disabled:pointer-events-none"
                              onClick={() => { focusAfter.current = `up-${i - 1}`; setFields(move(fields, i, -1)); setPicker(picker === i ? i - 1 : picker); }}><ChevronIcon size={16} className="rotate-180" /></IconButton>
                            <IconButton label={`Move ${f.label.trim() || `field ${i + 1}`} down`} data-focus={`down-${i}`} disabled={i === fields.length - 1} className="disabled:opacity-30 disabled:pointer-events-none"
                              onClick={() => { focusAfter.current = `down-${i + 1}`; setFields(move(fields, i, 1)); setPicker(picker === i ? i + 1 : picker); }}><ChevronIcon size={16} /></IconButton>
                            <IconButton label={`Remove ${f.label.trim() || `field ${i + 1}`}`} onClick={() => { setFields(removeField(fields, i)); setPicker(null); }}><TrashIcon size={16} /></IconButton>
                          </div>
                        </div>
                        <div className="pl-7 flex flex-col gap-2">
                          <ul className="m-0 p-0 list-none flex flex-wrap items-center gap-1.5" aria-label={`Source fields of ${f.label.trim() || `field ${i + 1}`}`}>
                            {f.paths.map((p, k) => (
                              <li key={sourceKey(p)} className="max-w-full">
                                <SourceChip s={p} unseen={discovered.data ? !knownKeys.has(sourceKey(p)) : false} onRemove={() => setFields(removeSource(fields, i, k))} />
                              </li>
                            ))}
                            <li>
                              <Button variant="ghost" className="h-7 px-2.5" aria-expanded={picker === i} data-focus={`add-${i}`} disabled={f.paths.length >= LIMITS.sources}
                                onClick={() => setPicker(picker === i ? null : i)}><PlusIcon size={14} />Add source</Button>
                            </li>
                          </ul>
                          {f.paths.length === 0 && <p className={cx('m-0 text-micro', sourceError ? 'text-fail' : 'text-n7')} role={sourceError ? 'alert' : undefined}>{sourceError ?? 'No source fields yet: add where this field lives in each system.'}</p>}
                          {f.paths.length > 0 && sourceError && <p className="m-0 text-micro text-fail" role="alert">{sourceError}</p>}
                          {picker === i && (
                            <SourcePicker known={known} loading={discovered.loading && !discovered.data} error={discovered.error} field={f} envName={current?.name ?? 'This environment'}
                              onAdd={(s) => setFields(addSource(fields, i, s))} onClose={() => { setPicker(null); focusAfter.current = `add-${i}`; setDraft({}); }} />
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
                <div>
                  <Button disabled={fields.length >= LIMITS.fields} onClick={() => { focusAfter.current = `label-${fields.length}`; setFields([...fields, emptyField()]); }}><PlusIcon size={14} />Add field</Button>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap p-5 pt-3 border-t border-n4">
              <span className="flex-1 min-w-[200px] text-small" aria-live="polite">
                {failure ? <span className="text-fail">{failure}</span>
                  : tried && total > 0 ? <span className="text-fail">{total === 1 ? '1 thing to fix' : `${total} things to fix`} before saving</span>
                    : <span className="text-n7">Saved as a draft: nothing changes for searches until you publish and deploy it.</span>}
              </span>
              <Button variant="ghost" onClick={cancel}>Cancel</Button>
              <Button type="submit" variant="primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
